import React, { useMemo } from "react";
import { Box, useTheme } from "@mui/material";
import { GripHorizontal, Trash2 } from "lucide-react";
import { TFunction } from "i18next";
import {
  DragDropContext,
  Draggable,
  DraggableProvidedDragHandleProps,
  Droppable,
  DropResult,
} from "react-beautiful-dnd";
import { useParams } from "react-router-dom";
import ListItemCardCst from "@components/ListItemCardCst";
import { mapEntityToCard } from "@pages/Contents/V2/Corporate/Catalog/mapEntityToCard";
import {
  EnrichedLO,
  useLOEntitiesByIds,
} from "@pages/Contents/V2/Corporate/Catalog/search/useLOEntitiesByIds";
import {
  LOCompletionCriteria,
  SelectedLOItem,
  SelectedLOListItem,
} from "@pages/Contents/V2/Corporate/Catalog/types";
import LOAlternatives from "@components/LOAlternatives";
import SectionTitleTranslation from "@pages/Contents/V2/Corporate/Catalog/components/SectionTitleTranslation";

interface SelectedLOListProps {
  items: SelectedLOListItem[];
  tCatalog: TFunction;
  removeLabel: string;
  onRemove: (id: number) => void;
  onRemoveFromSection?: (sectionIndex: number, id: number) => void;
  onEditSection?: (sectionIndex: number) => void;
  onRemoveSection?: (sectionIndex: number) => void;
  onDetails: (item: SelectedLOItem) => void;
  onReorder?: (fromIndex: number, toIndex: number) => void;
  reorderDisabled?: boolean;
  showDetailsAction?: boolean;
  completionCriteriaEnabled?: boolean;
  onCompletionCriteriaChange?: (
    item: SelectedLOItem,
    value: LOCompletionCriteria,
  ) => void;
}

type RenderLOProps = Omit<
  SelectedLOListProps,
  "items" | "onReorder" | "reorderDisabled"
> & {
  item: SelectedLOItem;
  enriched?: EnrichedLO;
  isAlternative?: boolean;
  removalLocked?: boolean;
  dragHandle?: React.ReactNode;
};

const isDraftItem = (item: SelectedLOItem): boolean => !!item.entity?.is_draft;

const DragHandle: React.FC<{
  color: string;
  handleProps?: DraggableProvidedDragHandleProps | null;
}> = ({ color, handleProps }) => (
  <Box
    {...handleProps}
    sx={{
      display: "flex",
      alignItems: "center",
      flexShrink: 0,
      color,
      cursor: "grab",
      userSelect: "none",
      opacity: 0.5,
    }}
  >
    <GripHorizontal size={18} />
  </Box>
);

function RenderLO({
  item,
  enriched,
  onDetails,
  onRemove,
  tCatalog,
  removeLabel,
  isAlternative,
  showDetailsAction = false,
  removalLocked = false,
  completionCriteriaEnabled,
  onCompletionCriteriaChange,
  dragHandle,
}: RenderLOProps) {
  const isDraft = isDraftItem(item);
  const entity = isDraft
    ? { ...item.entity, publish_date: null }
    : enriched
      ? { ...item.entity, ...enriched.entity }
      : item.entity;
  // Il dettaglio del contenuto padre non distingue corsi e attività figlie:
  // quando disponibile vince il tipo dell'indice aggregato.
  const entityType = enriched?.entityType ?? item.kind;

  const cardProps = mapEntityToCard(entity, entityType, {
    t: tCatalog,
    showAlternativeSectionCount: true,
    onDetails: () => onDetails(item),
  });

  const { metrics: _metrics, ...rest } = cardProps;

  const badges = isDraft
    ? [
        ...(cardProps.badges || []),
        {
          label: String(tCatalog("badges.draft")),
          variant: "warning" as const,
        },
      ]
    : cardProps.badges;

  return (
    <ListItemCardCst
      {...rest}
      badges={badges}
      backgroundVariant={isAlternative ? "default" : "card"}
      dragHandle={dragHandle}
      leftDivider={showDetailsAction}
      mandatorySelection={
        isAlternative
          ? undefined
          : completionCriteriaEnabled && !!onCompletionCriteriaChange
      }
      mandatorySelected={item.completion_criteria === "required"}
      onMandatorySelect={(selected) =>
        onCompletionCriteriaChange?.(item, selected ? "required" : null)
      }
      actions={[
        ...(showDetailsAction ? cardProps.actions || [] : []),
        {
          label: removeLabel,
          icon: <Trash2 size={18} />,
          iconOnly: true,
          variant: showDetailsAction ? "ghost" : undefined,
          disabled: !!item.locked || removalLocked,
          onClick: () => {
            if (item.locked || removalLocked) return;
            onRemove(item.id);
          },
        },
      ]}
    />
  );
}

const getDraggableId = (item: SelectedLOListItem, index: number) =>
  `${item.kind}-${item.id ?? `index-${index}`}`;

const SelectedLOList: React.FC<SelectedLOListProps> = ({
  items,
  onReorder,
  reorderDisabled = false,
  ...props
}) => {
  const theme = useTheme();
  const { id: corporateId = "1" } = useParams();
  const canReorder = !!onReorder && !reorderDisabled && items.length > 1;

  const idsToEnrich = useMemo(() => {
    const ids: number[] = [];
    const collect = (lo: SelectedLOItem) => {
      if (isDraftItem(lo) || lo.entity?.metadata_counters) return;
      if (Number.isFinite(lo.id)) ids.push(lo.id);
    };
    items.forEach((item) => {
      if (item.kind === "section") item.items.forEach(collect);
      else collect(item);
    });
    return ids;
  }, [items]);

  const { entitiesById } = useLOEntitiesByIds({
    corporateId,
    ids: idsToEnrich,
  });

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;
    onReorder?.(result.source.index, result.destination.index);
  };

  const renderItem = (
    item: SelectedLOListItem,
    index: number,
    handleProps?: DraggableProvidedDragHandleProps | null,
  ) => {
    if (item.kind !== "section") {
      return (
        <RenderLO
          item={item}
          enriched={entitiesById.get(item.id)}
          {...props}
          dragHandle={
            canReorder && (
              <DragHandle
                color={theme.palette.common.white}
                handleProps={handleProps}
              />
            )
          }
        />
      );
    }

    return (
      <LOAlternatives
        title={item.title}
        titleAdornment={<SectionTitleTranslation sectionId={item.id} />}
        actionsDisabled={!!item.locked}
        onEditSection={
          props.onEditSection ? () => props.onEditSection?.(index) : undefined
        }
        onRemoveSection={
          props.onRemoveSection
            ? () => props.onRemoveSection?.(index)
            : undefined
        }
        dragHandle={
          canReorder && (
            <DragHandle
              color={theme.palette.text.tertiary}
              handleProps={handleProps}
            />
          )
        }
      >
        {item.items.map((i) => (
          <RenderLO
            key={`alternative-${item.id ?? index}-${i.kind}-${i.id}`}
            isAlternative
            removalLocked={!!item.locked}
            item={i}
            enriched={entitiesById.get(i.id)}
            {...props}
            onRemove={(id) => props.onRemoveFromSection?.(index, id)}
          />
        ))}
      </LOAlternatives>
    );
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId="selected-lo-list" isDropDisabled={!canReorder}>
        {(droppableProvided) => (
          <Box
            ref={droppableProvided.innerRef}
            {...droppableProvided.droppableProps}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            {items.map((item, index) => {
              const draggableId = getDraggableId(item, index);
              return (
                <Draggable
                  key={draggableId}
                  draggableId={draggableId}
                  index={index}
                  isDragDisabled={!canReorder}
                >
                  {(draggableProvided) => (
                    <Box
                      ref={draggableProvided.innerRef}
                      {...draggableProvided.draggableProps}
                      sx={{ pb: index < items.length - 1 ? 1.5 : 0 }}
                    >
                      {renderItem(
                        item,
                        index,
                        draggableProvided.dragHandleProps,
                      )}
                    </Box>
                  )}
                </Draggable>
              );
            })}
            {droppableProvided.placeholder}
          </Box>
        )}
      </Droppable>
    </DragDropContext>
  );
};

export default SelectedLOList;
